// Town-level landing pages for the four counties the Google Ads campaign
// targets (Anne Arundel, Howard, Baltimore, Carroll).
//
// Every entry is written to stand on its own. Near-duplicate city pages with
// the name swapped are treated as doorway pages and get filtered out of search
// results, which is worse than having no page at all — so each town describes
// its actual housing stock, terrain, access constraints and tree mix. Nothing
// here claims specific past jobs or customer counts; it describes the area and
// how the work applies there.
//
// Facts kept deliberately general and verifiable: emerald ash borer is
// established statewide, Callery/Bradford pears split in storms, and trees in
// the public right-of-way fall under Maryland's Roadside Tree Law and need a
// state permit before anyone touches them.

export const SERVICE_AREAS = [
  {
    slug: 'glen-burnie',
    name: 'Glen Burnie',
    county: 'Anne Arundel County',
    metaTitle: 'Tree Service Glen Burnie MD | Removal & Trimming | One Choice',
    metaDescription:
      'Licensed & insured tree removal, trimming and stump grinding in Glen Burnie, MD. Tight-access work in older Anne Arundel neighborhoods. Free quotes: (443) 208-0747.',
    heroLine: 'Tree Service in Glen Burnie, Maryland',
    intro:
      "Glen Burnie's neighborhoods went up fast in the post-war decades, and the shade trees planted with them are now sixty and seventy years old. That's a lot of full-grown oak and silver maple standing very close to rooflines, driveways and property lines that were drawn before anyone pictured the canopy at full size.",
    notes: [
      {
        title: 'Big trees, small side yards',
        body: "The typical lot here leaves a narrow gap between the house and the fence line. We rig and lower in sections rather than felling, so there's no swing room needed and nothing lands on the shed, the fence or the neighbor's driveway."
      },
      {
        title: 'Silver maples are the usual call',
        body: "They grow fast, get big, and shed limbs in summer storms — a lot of Glen Burnie removals start as a dropped limb. If yours is shedding deadwood over a roof, that's worth a look before the next storm rather than after."
      },
      {
        title: 'Street trees are not your call to make',
        body: "Trees in the public right-of-way fall under Maryland's Roadside Tree Law and need a state permit before any work happens. We'll tell you which side of the line your tree is on before we quote it."
      }
    ],
    species: 'Silver maple, white and red oak, sweetgum, Callery pear',
    nearby: ['pasadena', 'severn', 'annapolis']
  },
  {
    slug: 'pasadena',
    name: 'Pasadena',
    county: 'Anne Arundel County',
    metaTitle: 'Tree Service Pasadena MD | Waterfront Tree Removal | One Choice',
    metaDescription:
      'Tree removal, trimming and storm cleanup in Pasadena, MD. Waterfront and creek-lot work on the Magothy and Patapsco peninsulas. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Service in Pasadena, Maryland',
    intro:
      'Pasadena sits on the peninsula between the Magothy and the Patapsco, and that shapes the tree work. Properties near the water take wind from directions inland lots never feel, and the ground behind a bulkhead or along a creek bank behaves differently under a crane or a chip truck than a flat suburban yard does.',
    notes: [
      {
        title: 'Wind exposure changes what fails',
        body: "On open water frontage it is rarely the whole tree — it is the leaning pine, the one-sided crown, or the limb that has been levering against a prevailing wind for years. Those get spotted on the assessment, not after they land."
      },
      {
        title: 'Loblolly pines near the shoreline',
        body: 'Pines close to the Bay grow tall and slim with the weight up top. They snap rather than uproot, and they do it in the direction the storm is going, which is why a pine over a dock or a boat lift is worth dealing with on a calm day.'
      },
      {
        title: 'Soft ground and shoreline access',
        body: "We plan the approach before the equipment arrives — matting where it's needed, or rigging the tree out to a hard surface instead of driving to it. Rutted lawn and a cracked bulkhead cost more to fix than the tree job did."
      }
    ],
    species: 'Loblolly pine, red maple, tulip poplar, oak',
    nearby: ['glen-burnie', 'annapolis', 'severn']
  },
  {
    slug: 'severn',
    name: 'Severn',
    county: 'Anne Arundel County',
    metaTitle: 'Tree Service Severn MD | Removal, Trimming & Lot Clearing',
    metaDescription:
      'Licensed tree removal, trimming and lot clearing in Severn, MD near Fort Meade and Odenton. Wooded-lot and new-construction work. Free estimates: (443) 208-0747.',
    heroLine: 'Tree Service in Severn, Maryland',
    intro:
      "Severn is where older Anne Arundel parcels meet the newer building around Fort Meade and Odenton. A lot of properties here back onto the woods that were left standing when the subdivision went in — which is fine until the edge trees, grown up sheltered inside a stand, suddenly find themselves exposed on one side.",
    notes: [
      {
        title: 'Edge trees behave differently',
        body: 'A tree that spent forty years inside a stand never built the taper or the root plate to stand alone. When the lot next door is cleared, the new edge is the first thing to come down in a storm. Worth assessing when the ground beside you changes.'
      },
      {
        title: 'Lot clearing that leaves something',
        body: "Selective clearing keeps the screening you actually want and takes out what's in the way of the build. We'd rather walk it with you and mark trees than clear to the stakes and have you plant it back in five years."
      },
      {
        title: 'Ash is mostly gone',
        body: 'Emerald ash borer has moved through Maryland and standing dead ash is brittle and unpredictable to climb. If you have grey, bark-shedding ash on the property, it gets removed as a hazard tree, not a routine one.'
      }
    ],
    species: 'Tulip poplar, red oak, Virginia pine, dead ash',
    nearby: ['glen-burnie', 'pasadena', 'columbia']
  },
  {
    slug: 'annapolis',
    name: 'Annapolis',
    county: 'Anne Arundel County',
    metaTitle: 'Tree Service Annapolis MD | Removal & Storm Cleanup | One Choice',
    metaDescription:
      'Tree removal, pruning and 24/7 storm response in Annapolis, MD. Tight historic-district access and waterfront properties. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Service in Annapolis, Maryland',
    intro:
      "Annapolis asks two different things of a tree crew. Downtown and in the historic streets it's an access problem — brick sidewalks, parked cars, power lines and no room to drop anything. Out toward the water it's an exposure problem, with mature trees taking the full weight of a nor'easter or the tail of a hurricane coming up the Bay.",
    notes: [
      {
        title: 'No room to drop it',
        body: 'On the older streets the work is done from rope and saddle, piece by piece, with the drop zone measured in feet. That is slower and it is the only way it gets done without touching the house, the wall or the car behind you.'
      },
      {
        title: 'Storms come up the Bay',
        body: "Remnant tropical systems and nor'easters hit waterfront properties here harder than the forecast for the county suggests. After a blow, the calls are leaning trees and hangers — the limb that came loose but did not come down."
      },
      {
        title: 'Old trees are worth keeping',
        body: 'Not every mature tree needs removing. Crown reduction and deadwood removal buy years on a good tree, and we will say so when that is the honest answer — you are paying for the assessment either way.'
      }
    ],
    species: 'Willow oak, tulip poplar, southern red oak, holly',
    nearby: ['pasadena', 'glen-burnie', 'severn']
  },
  {
    slug: 'ellicott-city',
    name: 'Ellicott City',
    county: 'Howard County',
    metaTitle: 'Tree Service Ellicott City MD | Steep-Lot Removal | One Choice',
    metaDescription:
      'Tree removal, trimming and storm cleanup in Ellicott City, MD. Steep hillside lots and tight historic access handled properly. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Service in Ellicott City, Maryland',
    intro:
      'Ellicott City is built on hillsides above the Patapsco, and the terrain does most of the deciding. Steep driveways, retaining walls and lots that fall away behind the house rule out simply driving equipment to the tree, and the town has learned the hard way what fast water off those slopes can do.',
    notes: [
      {
        title: 'Slope decides the method',
        body: 'On a hillside lot the wood has to be moved uphill or rigged out to the street, and a bucket truck often cannot get an angle at all. That is a climbing job, and pricing it any other way means someone is guessing.'
      },
      {
        title: 'Trees on a slope that has moved',
        body: 'After the flooding this area has seen, some hillside trees are holding soil that is no longer where it started. A leaning tree on a scoured bank is a different assessment than a leaning tree on flat ground — sometimes the root plate is the real problem.'
      },
      {
        title: 'Historic streets, no swing room',
        body: 'Close to Main Street the drop zones are tiny and the walls beside them are stone and old. Everything comes down on rope, and the site is swept properly at the end.'
      }
    ],
    species: 'Tulip poplar, white oak, black locust, sycamore',
    nearby: ['columbia', 'catonsville', 'highland']
  },
  {
    slug: 'columbia',
    name: 'Columbia',
    county: 'Howard County',
    metaTitle: 'Tree Service Columbia MD | Tree Removal & Pruning | One Choice',
    metaDescription:
      'Licensed tree removal, trimming and stump grinding in Columbia, MD. Village lots, mature canopy and open-space boundary questions. Free quotes: (443) 208-0747.',
    heroLine: 'Tree Service in Columbia, Maryland',
    intro:
      'Columbia was laid out to keep its trees, and it worked — the villages are genuinely wooded, with mature canopy right up against houses that were built into it on purpose. The catch is that the tree causing you trouble is not always on your land, and sorting that out first saves everyone a conversation later.',
    notes: [
      {
        title: 'Whose tree is it',
        body: 'A lot of Columbia lots back directly onto village open space, and those trees are not the homeowner\'s to remove. We check the line before quoting rather than after — if it is not yours, we will tell you who to call.'
      },
      {
        title: 'Canopy grown into the house',
        body: 'Fifty-odd years on from planting, limbs are on rooflines and gutters across the villages. Clearance pruning done properly keeps the tree and gets it off the house; topping it does neither, and we will not do it.'
      },
      {
        title: 'HOA and covenant work',
        body: 'Where approval is needed before work starts, expect us to ask about it during the estimate. Turning up to a job that has not been cleared wastes your day and ours.'
      }
    ],
    species: 'Tulip poplar, red maple, white oak, dogwood',
    nearby: ['ellicott-city', 'highland', 'severn']
  },
  {
    slug: 'highland',
    name: 'Highland',
    county: 'Howard County',
    metaTitle: 'Tree Service Highland MD | Large-Lot Tree Removal | One Choice',
    metaDescription:
      'Tree removal, trimming and lot clearing in Highland, MD. Large rural Howard County properties, mature hardwoods and long driveways. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Service in Highland, Maryland',
    intro:
      "Highland is the rural end of Howard County — big parcels, long driveways, standing hardwood and open field edges. The work here tends to be a different shape than suburban tree service: more trees per visit, more room to work, and access that runs a few hundred feet from the road before it reaches anything.",
    notes: [
      {
        title: 'Priced by the visit, not the tree',
        body: 'On acreage it rarely makes sense to bring a crew out for one stem. Walk the property with us and we will price the whole list — the dead one by the barn, the leaner over the fence, the row along the drive — in a single trip.'
      },
      {
        title: 'Mature hardwood needs judgement',
        body: 'A two-hundred-year-old white oak is not replaceable and is usually worth saving. Structural pruning and deadwood removal are the right answer far more often than removal, and the assessment should tell you which one you are looking at.'
      },
      {
        title: 'Fence lines, paddocks and outbuildings',
        body: 'Board fence and livestock complicate a drop zone more than a garage does. We plan the felling direction and the clean-up route around what is actually in the field, and we leave gates the way we found them.'
      }
    ],
    species: 'White oak, hickory, black walnut, tulip poplar',
    nearby: ['columbia', 'ellicott-city', 'eldersburg']
  },
  {
    slug: 'towson',
    name: 'Towson',
    county: 'Baltimore County',
    metaTitle: 'Tree Service Towson MD | Tree Removal & Trimming | One Choice',
    metaDescription:
      'Tree removal, pruning and storm cleanup in Towson, MD. Established neighborhoods with large mature canopy over houses. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Service in Towson, Maryland',
    intro:
      'Towson has some of the oldest residential canopy in Baltimore County — streets where the crowns close overhead and the trunks predate the houses under them. Trees that size are an asset until they are a liability, and the difference is usually visible a season or two before anything falls.',
    notes: [
      {
        title: 'Large stems over occupied roofs',
        body: 'A mature oak leaning over a bedroom is a rigging job, not a felling job. Sections come down on rope to a controlled drop zone, and the crew works to the roof line rather than around it.'
      },
      {
        title: 'Deadwood is the early warning',
        body: 'Fine deadwood through the upper crown, thinning leaf cover, or a limb that dropped for no obvious reason — those show up before failure. A crown clean-out costs a fraction of an emergency removal and a new roof.'
      },
      {
        title: 'Neighbors close on both sides',
        body: 'On these lots the drop zone belongs to three households. We talk to the neighbors when the work needs their driveway or their side yard, rather than turning up with a chipper and hoping.'
      }
    ],
    species: 'White oak, tulip poplar, Norway maple, beech',
    nearby: ['catonsville', 'ellicott-city', 'glen-burnie']
  },
  {
    slug: 'catonsville',
    name: 'Catonsville',
    county: 'Baltimore County',
    metaTitle: 'Tree Service Catonsville MD | Removal & Stump Grinding',
    metaDescription:
      'Licensed tree removal, trimming and stump grinding in Catonsville, MD. Older streetcar-suburb lots with big street-side oaks. Free estimates: (443) 208-0747.',
    heroLine: 'Tree Service in Catonsville, Maryland',
    intro:
      'Catonsville grew up along the streetcar line, and the tree stock reflects it — deep front-yard oaks set close to the street, narrow driveways between houses, and utility lines threaded through crowns that were planted before the wires arrived.',
    notes: [
      {
        title: 'Wires through the crown',
        body: 'Where limbs are into the service drop, that changes the sequence of the job and sometimes means the utility has to be involved before we climb. We sort that out at the estimate instead of discovering it on the day.'
      },
      {
        title: 'Stumps in finished front yards',
        body: 'Grinding in an established lawn is as much about the clean-up as the cut — grindings hauled or backfilled, and the area left ready to seed rather than as a crater with a tarp over it.'
      },
      {
        title: 'Access down the side of the house',
        body: 'A lot of these lots have one narrow route from street to back yard. We size the equipment to the gap rather than making the gap fit the equipment.'
      }
    ],
    species: 'Willow oak, pin oak, silver maple, Norway maple',
    nearby: ['towson', 'ellicott-city', 'columbia']
  },
  {
    slug: 'westminster',
    name: 'Westminster',
    county: 'Carroll County',
    metaTitle: 'Tree Service Westminster MD | Removal & Storm Cleanup',
    metaDescription:
      'Tree removal, trimming and storm cleanup in Westminster, MD. Farm edges, windbreak rows and open-exposure Carroll County properties. (443) 208-0747.',
    heroLine: 'Tree Service in Westminster, Maryland',
    intro:
      'Carroll County around Westminster is open country, and open country means wind. Trees standing at a field edge or in an old windbreak row have grown up taking weather from one direction with nothing in front of them, and they fail differently than sheltered suburban trees do.',
    notes: [
      {
        title: 'Windbreak rows age out together',
        body: 'A row planted in the same season reaches the end of its life in the same decade. When one goes over, look at the rest of the row — that is usually a planned job rather than an emergency one.'
      },
      {
        title: 'Room to fell properly',
        body: 'Where there is genuinely space, felling whole and processing on the ground is faster and cheaper than rigging, and we will price it that way. Paying for climbing you do not need is just paying more.'
      },
      {
        title: 'Storm work on long lanes',
        body: 'A tree across a farm lane cuts off the property, not just the driveway. Those get treated as access-blocking and dealt with accordingly when we are called out after a blow.'
      }
    ],
    species: 'Black walnut, ash, Norway spruce, silver maple',
    nearby: ['eldersburg', 'highland', 'towson']
  },
  {
    slug: 'eldersburg',
    name: 'Eldersburg',
    county: 'Carroll County',
    metaTitle: 'Tree Service Eldersburg MD | Tree Removal & Trimming',
    metaDescription:
      'Licensed tree removal, trimming and stump grinding in Eldersburg and Sykesville, MD. Wooded subdivision lots near Liberty Reservoir. (443) 208-0747.',
    heroLine: 'Tree Service in Eldersburg, Maryland',
    intro:
      'The subdivisions around Eldersburg and Sykesville were dropped into standing woods, which is what makes them pleasant to live in and what puts full-grown timber twenty feet from the house. Most of the work here is managing trees that were mature before the foundation was poured.',
    notes: [
      {
        title: 'Construction-era root damage shows up late',
        body: 'Trees that had their roots cut or the grade changed during building can hold on for years and then decline quickly. Thinning crowns and early leaf drop on an otherwise healthy-looking tree are worth a look.'
      },
      {
        title: 'Woodland edge, same as Severn',
        body: 'Where a rear lot line meets the woods, the exposed edge trees carry the wind load for everything behind them. Those are the ones to inspect before storm season, not the ones in the middle.'
      },
      {
        title: 'Reservoir-side properties',
        body: 'Closer to Liberty Reservoir the ground is steeper and wetter than it looks from the driveway. We plan equipment routes for that rather than churning a lawn to get to the back.'
      }
    ],
    species: 'Tulip poplar, red oak, Virginia pine, red maple',
    nearby: ['westminster', 'highland', 'columbia']
  },

  // County-level pages — these target "tree service <county>" searches, which
  // run at least as much volume as town names. Same anti-doorway rule applies:
  // each describes the county's actual terrain and constraints, not reworded
  // copies of the town pages.
  {
    slug: 'anne-arundel-county',
    name: 'Anne Arundel County',
    county: 'Maryland',
    metaTitle: 'Tree Services in Anne Arundel County, MD | One Choice',
    metaDescription:
      'Tree removal, trimming and storm cleanup across Anne Arundel County — Glen Burnie, Pasadena, Severn, Annapolis and beyond. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Services in Anne Arundel County, Maryland',
    intro:
      "Anne Arundel runs from dense post-war suburbs in the north to Chesapeake shoreline in the east, and the tree work changes with it. Inland it's mature oaks and silver maples on tight lots; near the water it's wind-shaped pines on sandy soil, where storms find the weak wood first.",
    notes: [
      {
        title: 'Two different counties in one',
        body: "A takedown in Glen Burnie is a rigging job between houses; the same size tree on a Pasadena creek lot is an access problem — soft ground, bulkheads, and equipment that has to stay off the septic field. We scope both on the free assessment, not on the day of the job."
      },
      {
        title: 'The Critical Area buffer is real',
        body: "Within 1,000 feet of tidal waters, Maryland's Chesapeake Bay Critical Area rules can restrict what can be removed and may require mitigation. If your property touches that buffer, we'll flag it before quoting so you don't end up in a permit dispute."
      },
      {
        title: 'Storm season drives the calendar',
        body: "Summer thunderstorms and the occasional tropical remnant do most of the damage here. The leaning pine or overextended maple limb you've been watching is cheapest to deal with before the wind decides for you."
      }
    ],
    species: 'White and red oak, silver maple, loblolly pine, sweetgum, Callery pear',
    nearby: ['glen-burnie', 'pasadena', 'severn', 'annapolis']
  },
  {
    slug: 'howard-county',
    name: 'Howard County',
    county: 'Maryland',
    metaTitle: 'Tree Services in Howard County, MD | One Choice',
    metaDescription:
      'Tree removal, pruning and lot clearing across Howard County — Ellicott City, Columbia, Highland, Clarksville. Licensed & insured. Free quotes: (443) 208-0747.',
    heroLine: 'Tree Services in Howard County, Maryland',
    intro:
      "Howard County splits between Columbia's planned villages, Ellicott City's older stock, and the large rural-residential lots out west. The constant is the tulip poplar: it grows tall and fast in piedmont clay, and it's involved in more Howard County storm calls than any other tree.",
    notes: [
      {
        title: 'Tulip poplars get ahead of their owners',
        body: "They put on height quickly and carry long, heavy limbs high up. By the time one looks too big over the house, it usually is. Crown reduction on a schedule is much cheaper than an emergency call after a summer squall."
      },
      {
        title: 'Columbia has paperwork',
        body: "Many Columbia villages have covenant guidelines that touch on significant tree removal. We've found the quickest path is documenting the tree's condition in the quote — it gives the village board what it needs the first time."
      },
      {
        title: 'Western lots need a plan, not just a saw',
        body: "On the acreage around Highland, Clarksville and Woodbine, the challenge is scale: multiple trees, long drags to the chip truck, and cleanup that has to leave a big lawn actually clean. We quote the whole job including haul-out, so the number you hear is the number."
      }
    ],
    species: 'Tulip poplar, white oak, red maple, hickory, Virginia pine',
    nearby: ['ellicott-city', 'columbia', 'highland']
  },
  {
    slug: 'baltimore-county',
    name: 'Baltimore County',
    county: 'Maryland',
    metaTitle: 'Tree Services in Baltimore County, MD | One Choice',
    metaDescription:
      'Tree removal, trimming and stump grinding across Baltimore County — Towson, Catonsville and the older suburbs. Licensed & insured. Free quotes: (443) 208-0747.',
    heroLine: 'Tree Services in Baltimore County, Maryland',
    intro:
      "Baltimore County's inner suburbs were built under a canopy that's now eighty to a hundred years old. Towson, Catonsville, Parkville — these neighborhoods have some of the biggest residential oaks in the region, standing over houses, garages and streets that leave no room for shortcuts.",
    notes: [
      {
        title: 'Century oaks come down in pieces',
        body: "A mature white oak over a Towson roofline is a full rigging job: climbed, dismantled limb by limb, every section lowered on ropes. It takes longer than dropping a tree in a field, and it's the only way it gets done without a new hole in the roof."
      },
      {
        title: 'Know whose line it is',
        body: "Limbs into the service line to your house are the homeowner's problem; trees into the primary lines on the street are BGE's. We'll tell you which situation you have — and when the utility has to act first — before anyone touches anything."
      },
      {
        title: 'Old streets, tight equipment choices',
        body: "Narrow driveways, alley garages and on-street parking decide what equipment can reach the tree. We scout access on the assessment so the crew shows up with a plan instead of a surprise."
      }
    ],
    species: 'White and red oak, American beech, black walnut, Norway maple',
    nearby: ['towson', 'catonsville'],
  },
  {
    slug: 'carroll-county',
    name: 'Carroll County',
    county: 'Maryland',
    metaTitle: 'Tree Services in Carroll County, MD | One Choice',
    metaDescription:
      'Tree removal, storm cleanup and lot clearing across Carroll County — Westminster, Eldersburg and the surrounding farms. Licensed & insured. (443) 208-0747.',
    heroLine: 'Tree Services in Carroll County, Maryland',
    intro:
      "Carroll County is the most rural ground we cover: farmsteads, fence lines, windbreaks and big standalone hardwoods that have had a century of open field to grow into. The work out here is less about squeezing between houses and more about handling sheer size — and the dead ash the borer left behind.",
    notes: [
      {
        title: 'Standing dead ash is the priority call',
        body: "Emerald ash borer has killed ash across Maryland, and dead ash gets brittle fast — it sheds limbs unpredictably and becomes unsafe to climb within a couple of years. If you have gray, bark-shedding ash near the house, driveway or pasture fence, that's the tree to deal with first."
      },
      {
        title: 'Room to work can mean a smaller bill',
        body: "Where there's open field, a tree can sometimes be felled whole instead of dismantled piece by piece — less time on the rope, lower cost. We'll tell you honestly when your situation allows the simpler approach."
      },
      {
        title: 'Fence lines and windbreaks',
        body: "Storm-dropped limbs across a fence or lane are a working problem, not just a yard problem. We clear the access first, then handle the rest of the tree, and the debris leaves with us unless you want the wood."
      }
    ],
    species: 'White ash (declining), black locust, white oak, hackberry, silver maple',
    nearby: ['westminster', 'eldersburg']
  }
];

export const getServiceArea = slug =>
  SERVICE_AREAS.find(area => area.slug === slug);
